import { useState, useCallback, useRef, useEffect } from "react";
import _ from "lodash";

export const useUndoRedo = (maxHistorySize = 5) => {
  const [history, setHistory] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const isRestoringRef = useRef(false);
  const saveTimeoutRef = useRef(null);

  const canUndo = currentIndex > 0 && history.length > 0;
  const canRedo = currentIndex < history.length - 1;

  const saveState = useCallback((state) => {
    if (isRestoringRef.current) {
      return;
    }

    // Debounce rapid state saves
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      setHistory(prevHistory => {
        // Remove any states after current index (for new branch)
        const newHistory = prevHistory.slice(0, currentIndex + 1);

        // Add new state
        const newState = {
          state: JSON.parse(JSON.stringify(state)),
          timestamp: Date.now()
        };
        newHistory.push(newState);

        // Trim to max size if needed
        if (newHistory.length > maxHistorySize) {
          return newHistory.slice(-maxHistorySize);
        }

        return newHistory;
      });

      // Update current index properly
      setCurrentIndex(prevIndex => {
        const newHistoryLength = Math.min(currentIndex + 2, maxHistorySize);
        return newHistoryLength - 1;
      });

    }, 100); // 100ms debounce
  }, [currentIndex, maxHistorySize]);

  const undo = useCallback(() => {
    if (!canUndo) return null;

    isRestoringRef.current = true;
    const newIndex = currentIndex - 1;
    setCurrentIndex(newIndex);

    setTimeout(() => {
      isRestoringRef.current = false;
    }, 100);

    return _.cloneDeep(history[newIndex]?.state) || null;
  }, [canUndo, currentIndex, history]);

  const redo = useCallback(() => {
    if (!canRedo) return null;

    isRestoringRef.current = true;
    const newIndex = currentIndex + 1;
    setCurrentIndex(newIndex);

    setTimeout(() => {
      isRestoringRef.current = false;
    }, 100);

    return _.cloneDeep(history[newIndex]?.state) || null;
  }, [canRedo, currentIndex, history]);

  const clearHistory = useCallback(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    setHistory([]);
    setCurrentIndex(-1);
  }, []);

  const getCurrentState = useCallback(() => {
    return _.cloneDeep(history[currentIndex]?.state) || null;
  }, [history, currentIndex]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  return {
    saveState,
    undo,
    redo,
    canUndo,
    canRedo,
    clearHistory,
    getCurrentState,
    historyLength: history.length,
    currentIndex
  };
};